import {
  COOKIE_CATEGORIES,
  type CookieCategory,
} from '~/src/services/cookieTranslationService/types.ts'

/**
 * Consent-gated scripts (`<script type="text/plain" data-cookieconsent="...">`)
 * never execute before consent, so the cookies they would set can never be
 * observed by the cookie scan. The page's own `data-cookieconsent` markers are
 * therefore a second source of consent categories, alongside the cookies
 * classified for the site.
 */

const DECLARED_SCRIPT_SELECTOR = '[data-cookieconsent]'
const ELEMENT_NODE = 1

export function getPageDeclaredCategories(): CookieCategory[] {
  const declared = new Set<string>()

  document.querySelectorAll(DECLARED_SCRIPT_SELECTOR).forEach(element => {
    const category = element
      .getAttribute('data-cookieconsent')
      ?.trim()
      .toLowerCase()

    if (category) {
      declared.add(category)
    }
  })

  return COOKIE_CATEGORIES.filter(
    category => category !== 'necessary' && declared.has(category),
  )
}

function containsDeclaredElement(node: Node): boolean {
  if (node.nodeType !== ELEMENT_NODE) {
    return false
  }

  const element = node as Element

  return (
    element.matches(DECLARED_SCRIPT_SELECTOR) ||
    element.querySelector(DECLARED_SCRIPT_SELECTOR) !== null
  )
}

/**
 * Gated scripts are often injected after the widget has initialized (e.g.
 * tracking pixels added by SPA code once its own config has loaded), so the
 * categories present in the DOM at init time are not the full picture. Watches
 * for late-injected `data-cookieconsent` elements and reports the current set
 * of page-declared categories whenever one appears.
 */
export function watchPageDeclaredCategories({
  onDeclaredScriptsAdded,
}: {
  onDeclaredScriptsAdded: (declaredCategories: CookieCategory[]) => void
}): void {
  const observer = new MutationObserver(mutations => {
    const hasDeclaredScripts = mutations.some(mutation =>
      Array.from(mutation.addedNodes).some(containsDeclaredElement),
    )

    if (hasDeclaredScripts) {
      onDeclaredScriptsAdded(getPageDeclaredCategories())
    }
  })

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  })

  // Sync once at registration: scripts added between the caller's initial DOM
  // snapshot and observe() would otherwise never be reported.
  onDeclaredScriptsAdded(getPageDeclaredCategories())
}
